import React, { useEffect, useMemo, useState } from 'react';
import { Retailer } from '../../Types/Retailer';
import Dropdown from '../Dropdown';
import { translate } from '../../Services/translation';
import Listbox from '../Listbox';

const formatName = (name: string) => {
  return name
    .toLowerCase()
    .replace(/(?:^|\s)\S/g, (char) => char.toUpperCase());
};

interface RetailerListFilteringProps {
  retailerList: Retailer[];
  setActiveCountryFilter: (country: string | null) => void;
  setActiveCityFilter: (city: string | null) => void;
  filteredRetailerList: Retailer[];
  setActiveRetailer: (retailer: Retailer | null) => void;
  activeCountryFilter: string | null;
  activeCityFilter: string | null;
  activeRetailer: Retailer | null;
  isLoading: boolean;
}

const RetailerListFiltering = ({
  retailerList,
  filteredRetailerList,
  setActiveCountryFilter,
  setActiveCityFilter,
  setActiveRetailer,
  activeCountryFilter,
  activeCityFilter,
  activeRetailer,
  isLoading,
}: RetailerListFilteringProps) => {
  const [countryPickerIsOpen, setCountryPickerIsOpen] =
    useState<boolean>(false);
  const [cityPickerIsOpen, setCityPickerIsOpen] = useState<boolean>(false);
  const [retailerPickerIsOpen, setRetailerPickerIsOpen] =
    useState<boolean>(false);
  const filteredCityList = useMemo(() => {
    if (activeCountryFilter == null) {
      return [...new Set(retailerList?.map((retailer) => retailer.city))];
    } else {
      return [
        ...new Set(
          retailerList
            .filter((retailer) =>
              retailer.country
                ?.toString()
                .includes(
                  activeCountryFilter != null ? activeCountryFilter : ''
                )
            )
            .map((retailer) => retailer.city)
        ),
      ];
    }
  }, [activeCountryFilter, retailerList]);

  const uniqueCountries = useMemo(() => {
    return [...new Set(retailerList?.map((retailer) => retailer.country))];
  }, [retailerList]);

  useEffect(() => {
    setActiveCityFilter(null);
    setActiveRetailer(null);
  }, [activeCountryFilter]);

  useEffect(() => {
    setActiveRetailer(null);
  }, [activeCityFilter]);

  const countryOptions = useMemo(() => {
    const options = uniqueCountries
      .sort((a, b) => a.localeCompare(b))
      .map((country) => {
        return {
          label: formatName(country),
          value: country,
        };
      });

    options.unshift({
      label: translate('general.show-all'),
      value: '',
    });

    return options;
  }, [uniqueCountries]);

  const countryOptionList = useMemo(() => {
    return countryOptions.map((o) => ({
      value: o.value,
      label: o.label,
      selected: o.value == (activeCountryFilter ?? ''),
    }));
  }, [countryOptions, activeCountryFilter]);

  const citiesOptions = useMemo(() => {
    const options = filteredCityList
      .sort((a, b) => a.localeCompare(b))
      .map((city) => {
        return {
          label: formatName(city),
          value: city,
        };
      });

    options.unshift({
      label: translate('general.show-all'),
      value: '',
    });

    return options;
  }, [filteredCityList]);

  const cityOptionList = useMemo(() => {
    return citiesOptions.map((o) => ({
      value: o.value,
      label: o.label,
      selected: o.value == (activeCityFilter ?? ''),
    }));
  }, [citiesOptions, activeCityFilter]);

  const handleSetActiveRetailerInDropdown = (retailer: Retailer) => {
    if (retailer.position.lat == 0 || retailer.position.lng == 0) {
      setActiveRetailer(null);
      return;
    }
    if (activeRetailer) {
      setActiveRetailer(null);
      setTimeout(() => {
        setActiveRetailer(retailer);
      }, 200);
    } else {
      setActiveRetailer(retailer);
    }
  };

  const retailerOptions = useMemo(() => {
    const options = filteredRetailerList
      ?.sort((a, b) => a.name?.localeCompare(b.name))
      .map((retailer) => {
        return {
          label: retailer.name,
          value: retailer.name,
          object: retailer,
        };
      });

    options?.unshift({
      label: translate('general.show-all'),
      value: '',
      object: {
        name: '',
        position: {
          lat: 0,
          lng: 0,
        },
      },
    });

    return options;
  }, [filteredRetailerList]);

  const retailerOptionList = useMemo(() => {
    return retailerOptions.map((o) => ({
      value: o.value,
      label: o.label,
      selected: o.value == (activeRetailer?.name ?? ''),
      object: o.object,
    }));
  }, [retailerOptions]);

  const selectedRetailerOption = retailerOptionList.find(
    (o) => o.value == (activeRetailer?.name ?? '')
  );
  const selectedCityOption = cityOptionList.find(
    (o) => o.value == (activeCityFilter ?? '')
  );
  const selectedCountryOption = countryOptionList.find(
    (o) => o.value == (activeCountryFilter ?? '')
  );

  console.log(countryOptionList, cityOptionList, retailerOptionList);

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <div className="relative [&_label]:bg-white [&_label]:px-1">
        {countryOptions && (
          <>
            <Dropdown
              className="relative"
              placeholder={translate('retailers.country-placeholder')}
              options={countryOptions}
              selectedValue={activeCountryFilter ?? ''}
              onChange={(e) => setActiveCountryFilter(e.value)}
              disabled={isLoading}
              hideForDesktop={true}
            />
            <Listbox
              setSelectedOption={(option) =>
                setActiveCountryFilter(
                  countryOptions.find((o) => o.value == option.value)
                    ?.value ?? null
                )
              }
              selectedOption={selectedCountryOption}
              optionList={countryOptionList}
              isOpen={countryPickerIsOpen}
              setIsOpen={setCountryPickerIsOpen}
              inputSize="large"
              label={translate('retailers.country-select')}
              showStickyLabel
            />
          </>
        )}
      </div>
      <div className="relative [&_label]:bg-white [&_label]:px-1">
        {citiesOptions && (
          <>
            <Dropdown
              className="relative"
              placeholder={translate('retailers.city-placeholder')}
              options={citiesOptions}
              selectedValue={activeCityFilter ?? ''}
              onChange={(e) => setActiveCityFilter(e.value)}
              disabled={isLoading}
              hideForDesktop={true}
            />
            <Listbox
              setSelectedOption={(option) =>
                setActiveCityFilter(
                  citiesOptions.find((o) => o.value == option.value)
                    ?.value ?? null
                )
              }
              selectedOption={selectedCityOption}
              optionList={cityOptionList}
              isOpen={cityPickerIsOpen}
              setIsOpen={setCityPickerIsOpen}
              inputSize="large"
              label={translate('retailers.city-select')}
              showStickyLabel
            />
          </>
        )}
      </div>
      <div className="relative [&_label]:bg-white [&_label]:px-1">
        {retailerOptions && (
          <>
            <Dropdown
              className="relative"
              placeholder={translate('retailers.retailer-placeholder')}
              options={retailerOptions}
              selectedValue={activeRetailer?.name ?? ''}
              onChange={(e) => handleSetActiveRetailerInDropdown(e.object)}
              disabled={isLoading}
              hideForDesktop={true}
            />
            <Listbox
              setSelectedOption={(option) =>
                handleSetActiveRetailerInDropdown(option.object)
              }
              selectedOption={selectedRetailerOption}
              optionList={retailerOptionList}
              isOpen={retailerPickerIsOpen}
              setIsOpen={setRetailerPickerIsOpen}
              inputSize="large"
              label={translate('retailers.retailer-select')}
              showStickyLabel
            />
          </>
        )}
      </div>
    </div>
  );
};

export default RetailerListFiltering;
